import { type LinkProps } from '@tanstack/react-router';

import type { FileRoutesByTo } from '@/routeTree.gen';

type RoutePath = LinkProps['to'];

/** Returns the fully-validated search param shape for a route, keyed by its navigable `to` path. */
type RouteSearch<TPath extends keyof FileRoutesByTo> =
  FileRoutesByTo[TPath]['types']['fullSearchSchema'];

/**
 * Declares which search params to carry over after a global filter change, per route.
 *
 * - Routes listed here: the specified keys are preserved; everything else resets to defaults.
 * - Routes not listed: all search params reset to schema defaults.
 *
 * TypeScript validates each key against the route's actual search schema,
 * so a typo or a removed param will cause a compile error.
 */
type FilterConfig = {
  [K in NonNullable<RoutePath>]?: K extends keyof FileRoutesByTo
    ? readonly (keyof RouteSearch<K>)[]
    : never;
};

const filterConfig: FilterConfig = {
  '/algorithm-tools/offline-sales-optimizer': ['opt', 'diag', 'iex'],
  '/algorithm-tools/starvation-bidder': ['diag', 'starve'],
};

/**
 * Navigation options returned for a given route after a global filter change.
 * The `to` field discriminates between routes that preserve search params and those that reset them.
 */
export type FilterNavigateOptions =
  | {
      to: RoutePath;
      search: (prev: Record<string, unknown>) => Record<string, unknown>;
      resetScroll: false;
    }
  | {
      to: RoutePath;
      resetScroll: false;
    };

/**
 * Routes scoped to a specific entity (e.g. a media plan id) that becomes
 * invalid when the user switches advertiser/account. Each entry maps a
 * pathname matcher to the listing route the user should be redirected to.
 */
const filterRedirects: { match: RegExp; to: RoutePath }[] = [
  { match: /^\/media-plans\/\d+/, to: '/media-plans' },
];

/**
 * Builds the `navigate()` options to use after a global filter change.
 *
 * - Routes matching a `filterRedirects` entry navigate to the listing route.
 * - Routes listed in `filterConfig` get a `search` callback that carries over
 *   the declared params.
 * - All other routes navigate to the current pathname with no `search`,
 *   resetting params to defaults.
 */
export function getFilterNavigateOptions(
  pathname: string
): FilterNavigateOptions {
  const redirect = filterRedirects.find(({ match }) => match.test(pathname));
  if (redirect) {
    return { to: redirect.to, resetScroll: false };
  }

  const to = pathname as RoutePath;
  let filterNavOpts: FilterNavigateOptions = { to, resetScroll: false };

  if (to && to in filterConfig) {
    const preservedKeys = filterConfig[to as keyof typeof filterConfig];

    if (preservedKeys) {
      filterNavOpts = {
        to,
        search: (prev) =>
          Object.fromEntries(preservedKeys.map((key) => [key, prev[key]])),
        resetScroll: false,
      };
    }
  }

  return filterNavOpts;
}
